/**
 * Tâche 1406 (Eric 2026-09-30) — « Quand une transcription a les vrais noms et
 * l'autre des Speaker A/B, propose un bouton pour attribuer automatiquement
 * les locuteurs les plus probables. »
 *
 * Le critère est le CHEVAUCHEMENT DE TEMPS DE PAROLE (choix d'Eric), pas
 * l'ordre d'apparition : dès qu'une source manque une intervention, l'ordre
 * décale tout le reste et associe systématiquement les mauvaises personnes.
 * Le temps commun, lui, reste juste même si une source découpe autrement ou
 * rate un passage — c'est le même raisonnement qu'en 1397 pour l'écart entre
 * sources.
 *
 * Rien n'est appliqué sans confirmation (choix d'Eric) : la proposition porte
 * son pourcentage de temps commun, pour qu'on voie tout de suite laquelle est
 * solide et laquelle est un coup de dé.
 */

// « Speaker A », « SPEAKER_01 », « Locuteur 2 », « Interlocuteur B »… — tout
// ce qui n'est PAS un nom de personne. Volontairement étroit : prendre un vrai
// nom pour un libellé générique le ferait écraser par une proposition.
const MOTIFS_GENERIQUES = [
  /^speaker[\s_-]*[a-z0-9]{1,3}$/i,
  /^locuteur[\s_-]*[a-z0-9]{1,3}$/i,
  /^interlocuteur[\s_-]*[a-z0-9]{1,3}$/i,
  /^spk[\s_-]*[a-z0-9]{1,3}$/i,
  /^participant[\s_-]*[a-z0-9]{1,3}$/i,
  /^inconnu$/i,
  /^unknown$/i,
];

function estGenerique(nom) {
  const n = String(nom || "").trim();
  if (!n) return true;
  return MOTIFS_GENERIQUES.some((m) => m.test(n));
}

/** Secondes de recouvrement entre deux intervalles. */
function recouvrement(a, b) {
  const debut = Math.max(Number(a.start) || 0, Number(b.start) || 0);
  const fin = Math.min(Number(a.end) || 0, Number(b.end) || 0);
  return Math.max(0, fin - debut);
}

function tempsParLocuteur(segments) {
  const par = {};
  for (const s of segments || []) {
    const nom = String(s.speaker || "").trim();
    if (!nom) continue;
    par[nom] = (par[nom] || 0) + Math.max(0, (Number(s.end) || 0) - (Number(s.start) || 0));
  }
  return par;
}

/**
 * @param {Array} segmentsNommes — la source qui PORTE les vrais noms.
 * @param {Array} segmentsGeneriques — la source à compléter.
 * @returns {Array<{generique, propose, secondesCommunes, secondesGenerique, pct, concurrent}>}
 *          Classé du plus sûr au plus douteux. `concurrent` nomme le second
 *          meilleur candidat : quand il est proche, la proposition est un
 *          coup de dé et la personne qui relit doit le savoir.
 */
function proposerAppariements(segmentsNommes, segmentsGeneriques) {
  const nommes = (segmentsNommes || []).filter((s) => !estGenerique(s.speaker));
  const cibles = (segmentsGeneriques || []).filter((s) => estGenerique(s.speaker));
  if (!nommes.length || !cibles.length) return [];

  const dureeCible = tempsParLocuteur(cibles);
  // { generique: { nom: secondes communes } }
  const croise = {};
  for (const c of cibles) {
    const g = String(c.speaker).trim();
    croise[g] = croise[g] || {};
    for (const n of nommes) {
      const sec = recouvrement(c, n);
      if (sec <= 0) continue;
      const nom = String(n.speaker).trim();
      croise[g][nom] = (croise[g][nom] || 0) + sec;
    }
  }

  const lignes = [];
  for (const g of Object.keys(croise)) {
    const classement = Object.entries(croise[g]).sort((a, b) => b[1] - a[1]);
    if (!classement.length) continue;
    const [propose, secondesCommunes] = classement[0];
    const total = dureeCible[g] || 0;
    lignes.push({
      generique: g,
      propose,
      secondesCommunes,
      secondesGenerique: total,
      pct: total > 0 ? Math.round((secondesCommunes / total) * 100) : 0,
      concurrent: classement[1] ? classement[1][0] : null,
      concurrentPct: classement[1] && total > 0
        ? Math.round((classement[1][1] / total) * 100)
        : 0,
    });
  }
  // À égalité de pourcentage, l'ordre alphabétique : sans second critère la
  // liste se réordonnerait sous le curseur d'un rafraîchissement à l'autre.
  return lignes.sort(
    (a, b) => b.pct - a.pct || String(a.generique).localeCompare(String(b.generique)),
  );
}

/** Seuil au-delà duquel la proposition est cochée d'office. */
const SEUIL_CONFIANCE_PCT = 50;

module.exports = {
  estGenerique,
  proposerAppariements,
  SEUIL_CONFIANCE_PCT,
};
